import { Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowBack, Search } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../hooks';
import { SearchInput } from './SearchInput';
import { setSearchTerm } from '../ducks/search';
import { getMainMenuItem, getSecondaryMenuItem, getSubMenuItem } from '../utils/menuUtils';
import { I18nKeys } from '../constants/I18nKeys';
import { unknownGroup } from '../constants/Group';
import { SalesViewUILayout } from '../constants/Viewer';
import { UserPreference } from '../constants/User';
import { PreferencesFormFields } from '../constants/FormFields';

export const LogoBarSearch: React.FC = () => {
  const { t } = useTranslation();
  const mobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const theme = useTheme();
  const greaterThanMid = useMediaQuery(theme.breakpoints.up('md'));

  const { clientId } = useAppSelector((state) => state.clientData);
  const { searchTerm: initialSearchValue, hideSearch } = useAppSelector((state) => state.search);
  const pricingState = useAppSelector((state) => state.pricing);
  const { mainMenuItem, subMenuItem, secondaryMenuItem } = useAppSelector((state) => state.menu);
  const currentUserState = useAppSelector((state) => state.currentUser);
  const { group: { groupId, configurators = [] } = unknownGroup } = currentUserState;
  const viewerState = useAppSelector((state) => state.viewer);
  // UI Layout Remove Me
  const layout = useAppSelector(
    (state) => state?.currentUser?.preferences?.[UserPreference.ProfilePreferences]?.[PreferencesFormFields.Layout],
  );

  const dispatch = useAppDispatch();
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState(initialSearchValue);

  const searchHandler = (searchString: string) => {
    setSearchValue(searchString);
    dispatch(setSearchTerm(searchString));
  };
  const debouncedSearchHandler = useMemo(() => debounce(searchHandler, 300), [setSearchValue]);

  // Stop the invocation of the debounced function after unmounting
  useEffect(
    () => () => {
      debouncedSearchHandler.cancel();
    },
    [],
  );

  const endSearch = (): void => {
    setSearchValue('');
    dispatch(setSearchTerm(''));
    setSearchExpanded(false);
  };

  const menuProps = useMemo(
    () => ({
      clientId,
      selectedGroupId: groupId,
      currentUserState,
      configurators,
      viewerState,
      pricingState,
      pricingEnableClientManaged: false,
    }),
    [clientId, groupId, currentUserState, configurators, viewerState, pricingState],
  );
  const { visible = false, label } = useMemo(() => {
    const secondarySearchProps = getSecondaryMenuItem(mainMenuItem, secondaryMenuItem)?.search;
    if (secondarySearchProps)
      return {
        visible: secondarySearchProps.visible(menuProps),
        label: secondarySearchProps.label,
      };

    const subSearchProps = getSubMenuItem(menuProps, mainMenuItem, dispatch, subMenuItem)?.search;
    if (subSearchProps) return { visible: subSearchProps.visible(menuProps), label: subSearchProps.label };

    const mainMenuSearchProps = getMainMenuItem(mainMenuItem)?.search;
    if (mainMenuSearchProps)
      return {
        visible: mainMenuSearchProps.visible(menuProps),
        label: mainMenuSearchProps.label,
      };

    return { visible: false, label: undefined };
  }, [menuProps, mainMenuItem, subMenuItem, secondaryMenuItem, dispatch]);

  if ((layout === SalesViewUILayout.MapNeeded && hideSearch) || (layout === SalesViewUILayout.Horizon && !visible))
    return null;
  return searchExpanded || greaterThanMid ? (
    <Box display="inline-block" sx={{ width: '240px', verticalAlign: 'middle' }}>
      <SearchInput
        id="logo-bar-search-input"
        searchTerm={searchValue}
        startAdornment={
          mobileView ? (
            <IconButton onClick={endSearch} size="large">
              <ArrowBack />
            </IconButton>
          ) : (
            <Search />
          )
        }
        placeholder={`${t(I18nKeys.SearchInputPlaceholder)} ${label?.(menuProps, t)}`}
        onClearClick={endSearch}
        onChange={(val: string) => {
          setSearchValue(val);
          debouncedSearchHandler(val);
        }}
      />
    </Box>
  ) : (
    <IconButton style={{ padding: 0 }} onClick={() => setSearchExpanded(true)} size="large">
      <Search />
    </IconButton>
  );
};
